import React, { ComponentType, useCallback } from 'react';
import {
  type ControllerProps,
  type FieldValues,
  type Path,
  type RegisterOptions,
  useController
} from 'react-hook-form';
import { type SxProps, type Theme } from '@mui/material';

import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import { useReadonly } from '@/hooks/useReadonly';

export type FieldOnChangeHandler<T extends unknown[]> = (...args: T) => void;

const Field = <
  TFieldValues extends object,
  TName extends Path<TFieldValues>,
  ComponentProps extends object,
  OnChangeHandlerArgs extends unknown[] = unknown[]
>(
  props: Omit<Omit<ControllerProps<TFieldValues, TName>, 'render'>, 'name'> & {
    component: ComponentType<ComponentProps>;
    name?: string;
    label?: string;
    rules?:
      | Omit<RegisterOptions<FieldValues, string>, 'setValueAs' | 'disabled' | 'valueAsNumber' | 'valueAsDate'>
      | undefined;
    withFormControl?: boolean;
    innerProps?: Partial<ComponentProps>;
    outerSx?: SxProps<Theme>;
    markAsRequired?: boolean;
    isDisabled?: boolean;
    disabled?: boolean;
    onChange?: FieldOnChangeHandler<OnChangeHandlerArgs>;
  }
) => {
  const { getPrefixedFieldName } = useFieldPrefix();
  const {
    component: Component,
    innerProps,
    name,
    label,
    rules,
    outerSx,
    withFormControl,
    markAsRequired,
    isDisabled,
    disabled
  } = props;
  const compoundName = getPrefixedFieldName(name);
  const { field, fieldState } = useController({ name: compoundName as TName, rules });

  const { isReadonly } = useReadonly();
  const finalDisabled = disabled || isDisabled || isReadonly;

  const value = field?.value ?? null;

  delete (field as { ref?: object }).ref; // We remove ref because underlying components are functional

  const handleChange = useCallback(
    (...args: OnChangeHandlerArgs) => {
      field?.onChange(...args);
      props.onChange?.(...args);
    },
    [field, props]
  );

  return (
    <Component
      {...field}
      {...fieldState}
      {...({
        label,
        value,
        withFormControl,
        outerSx,
        markAsRequired,
        isDisabled: finalDisabled,
        disabled: finalDisabled,
        ...innerProps
      } as ComponentProps)}
      onChange={handleChange}
    />
  );
};

export default Field;
